import { useCallback, useMemo, useState } from 'react';
import type { ApolloError } from '@apollo/client';

import type { UserRelationshipToWorkspace } from 'app/src/components/WorkspaceSwitcher';
import { useAllBoardsAndWorkspaces } from 'app/src/components/WorkspaceSwitcher';

const isMemberOfWorkspace = (
  idOrganizations: string[] = [],
  workspaceId: string | null,
): boolean => {
  return idOrganizations.some((orgId) => orgId === workspaceId);
};

export class MissingMemberWorkspaceRelationshipError extends Error {
  constructor() {
    const message = 'Data missing for relationship of member to workspace.';
    super(message);
    this.name = 'MissingMemberWorkspaceRelationshipError';
  }
}

interface WorkspaceBase {
  userRelationshipToWorkspace: UserRelationshipToWorkspace;
}

export interface PersonalWorkspace extends WorkspaceBase {
  userRelationshipToWorkspace: 'PERSONAL';
}
export interface GuestWorkspace extends WorkspaceBase {
  userRelationshipToWorkspace: 'GUEST';
}
export interface MemberWorkspace extends WorkspaceBase {
  userRelationshipToWorkspace: 'MEMBER';
}
export interface NoRelationshipWorkspace {
  userRelationshipToWorkspace: 'NONE';
}

export type Workspace =
  | GuestWorkspace
  | MemberWorkspace
  | NoRelationshipWorkspace
  | PersonalWorkspace;

interface UseMemberRelationshipWorkspaceReturn {
  userRelationshipToWorkspace: UserRelationshipToWorkspace | undefined;
  error: ApolloError | Error | undefined;
  loading: boolean;
  refetch: () => void;
}

interface UserRelationshipToWorkspacePresentReturn
  extends UseMemberRelationshipWorkspaceReturn {
  userRelationshipToWorkspace: UserRelationshipToWorkspace;
  error: undefined;
  loading: false;
}

interface UserRelationshipToWorkspaceErrorReturn
  extends UseMemberRelationshipWorkspaceReturn {
  userRelationshipToWorkspace: undefined;
  error: ApolloError | Error;
}

interface UserRelationshipToWorkspaceLoadingReturn
  extends UseMemberRelationshipWorkspaceReturn {
  loading: true;
}

export const useMemberWorkspaceRelationship = ({
  workspaceId,
  idBoard,
}: {
  workspaceId: string | null;
  idBoard?: string;
}):
  | UserRelationshipToWorkspaceErrorReturn
  | UserRelationshipToWorkspaceLoadingReturn
  | UserRelationshipToWorkspacePresentReturn => {
  const { guestOrganizations, data, error, loading, refetch } =
    useAllBoardsAndWorkspaces();

  // Create a unique error instance per-invocation of the query so we don't re-trigger
  // error-handling (Sentry, etc) in consumers when this hook is re-run
  const [refetchCount, setRefetchCount] = useState(0);
  const missingMemberWorkspaceRelationshipError = useMemo(
    () => new MissingMemberWorkspaceRelationshipError(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetchCount],
  );

  const userRelationshipToWorkspaceRefetch = useCallback(() => {
    refetch();
    setRefetchCount((count: number) => count + 1);
  }, [refetch]);

  if (error) {
    return {
      userRelationshipToWorkspace: undefined,
      error,
      loading: false,
      refetch: userRelationshipToWorkspaceRefetch,
    };
  }

  if (loading) {
    return {
      userRelationshipToWorkspace: undefined,
      error: undefined,
      loading,
      refetch: userRelationshipToWorkspaceRefetch,
    };
  }

  if (!data?.member) {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const error = missingMemberWorkspaceRelationshipError;
    return {
      userRelationshipToWorkspace: undefined,
      error,
      loading: false,
      refetch: userRelationshipToWorkspaceRefetch,
    };
  }

  const { member } = data;

  const idOrganizations = Array.from(member.organizations, (organization) => {
    return organization.id;
  });
  // Member of Workspace
  if (isMemberOfWorkspace(idOrganizations, workspaceId)) {
    return {
      userRelationshipToWorkspace: 'MEMBER',
      error,
      loading,
      refetch: userRelationshipToWorkspaceRefetch,
    };
  }

  const guestWorkspace = guestOrganizations.find(
    (org) => org.id === workspaceId,
  );

  // Guest of workspace
  if (guestWorkspace) {
    return {
      userRelationshipToWorkspace: 'GUEST',
      error,
      loading,
      refetch: userRelationshipToWorkspaceRefetch,
    };
  }

  const board = idBoard
    ? (member.boards.find((b) => b.id === idBoard) ?? null)
    : null;

  // Personal Workspace
  if (board !== null && !board.idOrganization) {
    return {
      userRelationshipToWorkspace: 'PERSONAL',
      error,
      loading,
      refetch: userRelationshipToWorkspaceRefetch,
    };
  }

  return {
    userRelationshipToWorkspace: 'NONE',
    error,
    loading,
    refetch: userRelationshipToWorkspaceRefetch,
  };
};
